/**
 * PrivilegeDetailPage — Detail d'une Escale Privilege.
 * - Visiteur : teaser + CTA "Devenir adherent" / "Se connecter"
 * - Adherent actif : full + bouton reveal selon access_type
 */
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getPublicPrivilege, revealPrivilege, getMedia } from "../lib/api";
import { getPublicMediaUrl } from "../lib/mediaUrl";
import { useAuth } from "../context/AuthContext";
import { usePublicMembershipEnabled } from "../components/PreLaunchGuard";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import {
  Sparkles, Lock, Calendar, Tag, ArrowLeft, Copy, ExternalLink,
  BookOpen, Ticket, Info, CheckCircle2, Loader2,
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const CATEGORY_LABELS = {
  croisieres: "Croisières",
  hotels: "Hôtels",
  sejours: "Séjours",
  excursions: "Excursions",
  accessoires: "Accessoires",
  bagagerie: "Bagagerie",
  autre: "Autre",
};

function MediaImage({ mediaId, className }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!mediaId) return;
    getMedia(mediaId).then((res) => setUrl(getPublicMediaUrl(res.data))).catch(() => setUrl(null));
  }, [mediaId]);
  if (!url) return <div className={`bg-secondary flex items-center justify-center ${className}`}><Sparkles className="h-12 w-12 opacity-20" /></div>;
  return <img src={url} alt="" className={className} />;
}

export default function PrivilegeDetailPage() {
  const { id } = useParams();
  const { user, isMember } = useAuth();
  const publicMembership = usePublicMembershipEnabled();
  const navigate = useNavigate();

  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealing, setRevealing] = useState(false);
  const [revealed, setRevealed] = useState(null); // {access_type, code|url|instructions}
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    getPublicPrivilege(id)
      .then((res) => setOffer(res.data))
      .catch((err) => {
        if (err?.response?.status === 404) {
          toast.error("Offre introuvable ou non disponible");
          navigate("/privileges");
        }
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleReveal = async () => {
    setRevealing(true);
    try {
      const res = await revealPrivilege(id);
      setRevealed(res.data);
      if (res.data.access_type === "external_link") {
        // On ouvre l'URL directement en plus d'afficher la modale
        window.open(res.data.url, "_blank", "noopener,noreferrer");
      }
      setDialogOpen(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Impossible de révéler cette offre";
      if (err?.response?.status === 501) {
        toast.info("Le paiement direct sera bientôt disponible pour cette offre");
      } else if (err?.response?.status === 429) {
        toast.error("Trop de tentatives, patientez une minute");
      } else if (err?.response?.status === 403) {
        toast.error("Adhésion active requise");
      } else {
        toast.error(msg);
      }
    } finally {
      setRevealing(false);
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copié dans le presse-papier");
    } catch {
      toast.error("Impossible de copier");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-20 space-y-6">
          <Skeleton className="h-8 w-40" />
          <Skeleton className="aspect-[16/9] w-full rounded-2xl" />
          <Skeleton className="h-10 w-3/4" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (!offer) return null;

  const isTeaser = !!offer.is_teaser;
  const endDate = offer.end_date ? format(new Date(offer.end_date), "dd MMMM yyyy", { locale: fr }) : null;
  const categoryLabel = CATEGORY_LABELS[offer.category] || offer.category;

  return (
    <div className="min-h-screen bg-secondary pt-24 pb-16" data-testid="privilege-detail-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-20">
        <Link
          to="/privileges"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6 transition-colors"
          data-testid="privilege-back-link"
        >
          <ArrowLeft className="h-4 w-4" /> Retour aux Escales Privilèges
        </Link>

        {/* Image */}
        <div className="relative aspect-[16/9] rounded-2xl overflow-hidden mb-8 bg-white">
          <MediaImage mediaId={offer.main_image_id} className="w-full h-full object-cover" />
          <div className="absolute top-4 left-4 flex gap-2">
            <Badge className="bg-primary/95 text-white border-0 backdrop-blur-sm">
              <Sparkles className="h-3 w-3 mr-1" />
              Escale Privilège
            </Badge>
            {categoryLabel && (
              <Badge variant="secondary" className="bg-white/95 backdrop-blur-sm">
                <Tag className="h-3 w-3 mr-1" />
                {categoryLabel}
              </Badge>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contenu */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-3">{offer.title}</h1>
              <p className="text-lg text-muted-foreground">{offer.hook}</p>
            </div>

            {!isTeaser && offer.description && (
              <div className="bg-white rounded-2xl border border-border p-6">
                <h2 className="font-heading text-lg font-semibold text-primary mb-3">Description</h2>
                <div className="prose prose-sm max-w-none text-muted-foreground whitespace-pre-wrap">
                  {offer.description}
                </div>
              </div>
            )}

            {!isTeaser && offer.conditions && (
              <div className="bg-white rounded-2xl border border-border p-6">
                <h2 className="font-heading text-lg font-semibold text-primary mb-3 flex items-center gap-2">
                  <Info className="h-4 w-4" /> Conditions
                </h2>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{offer.conditions}</p>
              </div>
            )}
          </div>

          {/* Sidebar CTA */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-border p-6 sticky top-28 space-y-4">
              {isTeaser ? (
                <>
                  <div className="text-center py-6 border-b border-border">
                    <Lock className="h-12 w-12 mx-auto text-primary/60 mb-3" />
                    <p className="font-heading text-lg font-semibold text-primary mb-1">
                      Offre réservée aux adhérents
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Adhérez à ITERCATUS pour débloquer les détails, le prix membre et le code promo.
                    </p>
                  </div>
                  <Link to="/adhesion" className="block" data-testid="privilege-cta-adhesion" style={publicMembership ? {} : { display: "none" }}>
                    <Button className="w-full">Devenir adhérent</Button>
                  </Link>
                  {!user && (
                    <Link to="/connexion" className="block" data-testid="privilege-cta-login">
                      <Button variant="outline" className="w-full">Se connecter</Button>
                    </Link>
                  )}
                </>
              ) : (
                <>
                  {offer.member_price != null && offer.public_price != null && offer.savings_percent > 0 && (
                    <div className="border-b border-border pb-4">
                      <div className="flex items-baseline gap-2 mb-2">
                        <span className="text-3xl font-bold text-primary">{offer.member_price.toFixed(2)} €</span>
                        <span className="text-lg text-muted-foreground line-through">{offer.public_price.toFixed(2)} €</span>
                      </div>
                      <Badge className="bg-green-100 text-green-700 border-green-200">
                        Économie de {offer.savings_amount?.toFixed(2)} € ({offer.savings_percent}%)
                      </Badge>
                    </div>
                  )}

                  {endDate && (
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Calendar className="h-4 w-4" /> Valable jusqu'au {endDate}
                    </p>
                  )}

                  {offer.access_type === "code" && (
                    <Button
                      className="w-full"
                      onClick={handleReveal}
                      disabled={revealing || !offer.code_present}
                      data-testid="privilege-reveal-code-btn"
                    >
                      {revealing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Ticket className="h-4 w-4 mr-2" />}
                      Afficher mon avantage
                    </Button>
                  )}
                  {offer.access_type === "external_link" && (
                    <Button
                      className="w-full"
                      onClick={handleReveal}
                      disabled={revealing || !offer.external_url_present}
                      data-testid="privilege-external-link-btn"
                    >
                      {revealing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-2" />}
                      Accéder à l'offre partenaire
                    </Button>
                  )}
                  {offer.access_type === "reservation" && (
                    <Button
                      className="w-full"
                      onClick={handleReveal}
                      disabled={revealing || !offer.reservation_instructions_present}
                      data-testid="privilege-reservation-btn"
                    >
                      {revealing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <BookOpen className="h-4 w-4 mr-2" />}
                      Voir les instructions de réservation
                    </Button>
                  )}
                  {offer.access_type === "direct_payment" && (
                    <Button className="w-full" disabled data-testid="privilege-direct-payment-btn">
                      Paiement direct — bientôt disponible
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modale reveal */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="privilege-reveal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-primary">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Votre avantage adhérent
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {revealed?.access_type === "code" && (
              <>
                <p className="text-sm text-muted-foreground">
                  Utilisez ce code promotionnel lors de votre commande chez notre partenaire :
                </p>
                <div className="bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-dashed border-primary/30 rounded-xl p-6 text-center">
                  <p className="font-mono text-2xl md:text-3xl font-bold text-primary tracking-wider break-all" data-testid="privilege-code-value">
                    {revealed.code}
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => handleCopy(revealed.code)}
                  data-testid="privilege-copy-code-btn"
                >
                  <Copy className="h-4 w-4 mr-2" /> Copier le code
                </Button>
              </>
            )}
            {revealed?.access_type === "external_link" && (
              <>
                <p className="text-sm text-muted-foreground">
                  L'offre partenaire s'est ouverte dans un nouvel onglet. Si ce n'est pas le cas, cliquez ci-dessous :
                </p>
                <a href={revealed.url} target="_blank" rel="noopener noreferrer" className="block">
                  <Button className="w-full" data-testid="privilege-external-open-btn">
                    <ExternalLink className="h-4 w-4 mr-2" /> Ouvrir l'offre partenaire
                  </Button>
                </a>
              </>
            )}
            {revealed?.access_type === "reservation" && (
              <>
                <p className="text-sm text-muted-foreground">
                  Voici les instructions pour bénéficier de cette offre :
                </p>
                <div className="bg-secondary rounded-xl p-4">
                  <p className="text-sm whitespace-pre-wrap" data-testid="privilege-reservation-instructions">
                    {revealed.instructions}
                  </p>
                </div>
              </>
            )}
            {revealed?.conditions && (
              <div className="text-xs text-muted-foreground border-t border-border pt-3">
                <p className="font-medium mb-1">Conditions :</p>
                <p className="whitespace-pre-wrap">{revealed.conditions}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialogOpen(false)} data-testid="privilege-reveal-close-btn">
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
